import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import "../Forms/SystemUserActionButtons.css";
import "../Forms/UserForm.css";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import {
  Snackbar,
  Alert as MuiAlert,
  Checkbox,
  FormControl,
  MenuItem,
  Chip,
  Box,
  ListItemText,
  Select,
  OutlinedInput,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  ListSubheader,
} from "@mui/material";
import {
  IoCalendarOutline,
  IoChevronDown,
  IoPricetagsOutline,
  IoSearch,
} from "react-icons/io5";

/** ---------------------- utils ---------------------- */
const formatDateDisplay = (d) => {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatDateApi = (d) => {
  if (!d) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};

const parseDateFromApi = (str) => {
  if (!str || typeof str !== "string") return null;
  const s = str.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [d, m, y] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return null;
};

const getAuthKey = () => localStorage.getItem("authKey");

const getDurationDays = (start, end) => {
  if (!(start instanceof Date) || !(end instanceof Date)) return null;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
};

const createUserFormSnapshot = (formData, selectedProject, projectAccess) => {
  const sortedIds = (values = []) =>
    Array.from(new Set(values.map((value) => String(value)))).sort();
  const projectIds = sortedIds(selectedProject);

  return JSON.stringify({
    name: formData.name || "",
    email: formData.email || "",
    adharnumber: formData.adharnumber || "",
    phone_number: formData.phone_number || "",
    role: String(formData.role || ""),
    group: sortedIds(formData.group),
    department_ids: sortedIds(formData.department_ids),
    projects: projectIds.map((projectId) => {
      const access = projectAccess[projectId] || {};
      return {
        project_id: projectId,
        start: formatDateApi(access.start),
        end: formatDateApi(access.end),
        equipment_group_ids: sortedIds(access.equipment_group_ids),
      };
    }),
  });
};

/** ===================================================================== */
export default function EditUser({ isSidebarExpanded }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    adharnumber: "",
    phone_number: "",
    role: "",
    group: [],
    department_ids: [],
  });

  const [roles, setRoles] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipmentGroupOptions, setEquipmentGroupOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState([]);
  const [projectAccess, setProjectAccess] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [projectError, setProjectError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [editingUserRoleName, setEditingUserRoleName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [deptSearch, setDeptSearch] = useState("");
  const [equipGroupSearch, setEquipGroupSearch] = useState({});

  // Improvement 3 — Project accordion collapse state
  const [collapsedProjects, setCollapsedProjects] = useState({});

  // Improvement 4 — Loading skeletons for dropdowns
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Improvement 5 — Unsaved changes warning
  const [isDirty, setIsDirty] = useState(false);
  const initialFormSnapshotRef = useRef(null);

  const navigate = useNavigate();
  const { user_id } = useParams();

  /** Designation dropdown helpers (search + select all) */
  const filteredDepartmentOptions = useMemo(() => {
    const q = deptSearch.trim().toLowerCase();
    if (!q) return departmentOptions;
    return departmentOptions.filter((o) => o.label?.toLowerCase().includes(q));
  }, [departmentOptions, deptSearch]);

  const allFilteredDeptSelected =
    filteredDepartmentOptions.length > 0 &&
    filteredDepartmentOptions.every((o) => formData.department_ids.includes(o.value));

  const handleDepartmentChange = (value) => {
    // "__all__" is the sentinel emitted by the Select All row.
    if (value.includes("__all__")) {
      const filteredIds = filteredDepartmentOptions.map((o) => o.value);
      setFormData((s) => ({
        ...s,
        department_ids: allFilteredDeptSelected
          ? s.department_ids.filter((id) => !filteredIds.includes(id))
          : Array.from(new Set([...s.department_ids, ...filteredIds])),
      }));
      return;
    }
    setFormData((s) => ({ ...s, department_ids: value }));
  };

  useEffect(() => {
    const userRole = localStorage.getItem("role_name");
    setIsAdmin(userRole === "Admin");
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get_all_roles`);
        setRoles(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      }
    };
    const fetchGroups = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get/group`);
        const groups = res?.data?.data || [];
        setGroupOptions(groups.map((g) => ({ value: g.group_id, label: g.group_name })));
      } catch (err) {
        console.error("Failed to fetch groups:", err);
      }
    };
    const fetchProjects = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get_project`);
        const projectData = res?.data?.data || [];
        console.log("Fetched projectData:", projectData);
        setProjects(projectData);
        const seed = projectData.reduce((acc, project) => {
          const parsedStart = project.project_start ? new Date(project.project_start) : null;
          const parsedEnd = project.project_end ? new Date(project.project_end) : null;
          acc[project.project_id] = {
            project_id: project.project_id,
            project_name: project.name || "",
            start: parsedStart instanceof Date && !Number.isNaN(parsedStart.getTime()) ? parsedStart : null,
            end: parsedEnd instanceof Date && !Number.isNaN(parsedEnd.getTime()) ? parsedEnd : null,
            equipment_group_ids: [],
          };
          return acc;
        }, {});
        console.log("Parsed projectAccess seed:", seed);
        setProjectAccess((prev) => ({ ...prev, ...seed }));
      } catch (err) {
        console.error("Failed to fetch projects:", err);
      }
    };
    const fetchDepartments = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/departments`);
        const departments = res?.data?.departments || [];
        setDepartmentOptions(departments.map((d) => ({ value: d.id, label: d.name })));
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      }
    };
    const fetchEquipmentGroups = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get_all_equipment_groups`);
        const options = (res?.data?.data || []).map((item) => ({
          value: item.id,
          label: `${item.equipment_group_code || ""} - ${item.equipment_group_name || ""}`.trim(),
        }));
        setEquipmentGroupOptions(options);
      } catch (err) {
        console.error("Failed to fetch equipment groups:", err);
      }
    };
    // Improvement 4 — fetch all in parallel, then clear loading state
    Promise.allSettled([fetchRoles(), fetchGroups(), fetchProjects(), fetchDepartments(), fetchEquipmentGroups()])
      .then(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    if (!user_id || roles.length === 0) return;
    (async () => {
      try {
        const response = await axiosInstance.get(
          `${API_URL}/api/get_user_by_id/${user_id}`,
          { headers: { Authorization: `Bearer ${getAuthKey()}` } }
        );
        const users = response.data?.data || [];
        if (!users.length) return;
        const user = users[0];
        const userRole = roles.find((r) => r.role_id === user.role_id);
        setEditingUserRoleName(userRole?.role_name || "");
        const assignedGroupIds = (user.assigned_group || []).map((g) => g.group_id);
        const assignedDepartmentIds = (user.assigned_departments || []).map((d) => d.department_id);
        const loadedFormData = {
          name: user.users_name || "",
          email: user.users_email || "",
          adharnumber: user.aadhar_number || "",
          phone_number: user.contact_number || "",
          role: user.role_id || "",
          group: assignedGroupIds,
          department_ids: assignedDepartmentIds,
        };
        const assigned = (user.assigned_projects || []).map((p) => ({
          project_id: p.project_id,
          project_name: p.project_name || "",
          start: parseDateFromApi(p.access_start),
          end: parseDateFromApi(p.access_end),
          equipment_group_ids: Array.isArray(p.equipment_groups)
            ? p.equipment_groups.map((g) => g.equipment_group_id)
            : [],
        }));
        const ids = assigned.map((x) => x.project_id);
        const accessObj = {};
        assigned.forEach(({ project_id, project_name, start, end, equipment_group_ids }) => {
          accessObj[project_id] = { project_id, project_name, start: start || null, end: end || null, equipment_group_ids: equipment_group_ids || [] };
        });
        ids.forEach((pid) => {
          if (!accessObj[pid]) {
            const projName = projects.find((p) => p.project_id === pid)?.name || "";
            accessObj[pid] = { project_id: pid, project_name: projName, start: null, end: null, equipment_group_ids: [] };
          }
        });
        initialFormSnapshotRef.current = createUserFormSnapshot(
          loadedFormData,
          ids,
          accessObj,
        );
        setFormData(loadedFormData);
        setSelectedProject(ids);
        setProjectAccess(accessObj);
        setIsDirty(false);
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    })();
  }, [user_id, roles, projects]);

  // Track changes against the user values loaded from the API. This avoids
  // treating asynchronous dropdown/project initialization as a user edit.
  useEffect(() => {
    if (!initialFormSnapshotRef.current) return;
    const currentSnapshot = createUserFormSnapshot(
      formData,
      selectedProject,
      projectAccess,
    );
    setIsDirty(currentSnapshot !== initialFormSnapshotRef.current);
  }, [formData, selectedProject, projectAccess]);

  // Improvement 5 — beforeunload warning
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Improvement 1 — inline validation on blur
  const handleBlur = (fieldName) => {
    setFieldErrors((prev) => {
      const errs = { ...prev };
      if (fieldName === "name") {
        if (!formData.name?.trim()) errs.name = "Name is required.";
        else delete errs.name;
      }
      if (fieldName === "email") {
        if (!formData.email?.trim()) errs.email = "Email is required.";
        else delete errs.email;
      }
      if (fieldName === "phone_number") {
        if (!formData.phone_number?.trim()) errs.phone = "Phone number is required.";
        else if (!/^\d{10}$/.test(formData.phone_number.trim())) errs.phone = "Phone number must be 10 digits.";
        else delete errs.phone;
      }
      if (fieldName === "adharnumber") {
        if (formData.adharnumber?.trim() && formData.adharnumber.trim().length !== 12)
          errs.aadhar = "Aadhar number must be 12 digits.";
        else delete errs.aadhar;
      }
      return errs;
    });
  };

  // Improvement 3 — accordion toggle
  const toggleProject = (pid) => setCollapsedProjects((prev) => ({ ...prev, [pid]: !prev[pid] }));

  const ensureProjectAccessKeys = (ids) => {
    setProjectAccess((prev) => {
      const next = { ...prev };
      ids.forEach((pid) => {
        const projectName = projects.find((p) => p.project_id === pid)?.name || "";
        if (!next[pid]) {
          next[pid] = { project_id: pid, project_name: projectName, start: null, end: null, equipment_group_ids: [] };
        } else {
          next[pid] = { ...next[pid], project_id: next[pid].project_id || pid, project_name: next[pid].project_name || projectName, equipment_group_ids: next[pid].equipment_group_ids || [] };
        }
      });
      Object.keys(next).forEach((pid) => { if (!ids.includes(pid)) delete next[pid]; });
      return next;
    });
  };

  const onProjectsChange = (e) => {
    const ids = e.target.value;
    setSelectedProject(ids);
    setProjectError("");
    ensureProjectAccessKeys(ids);
  };

  const setAccessStart = (pid, date) => {
    setProjectAccess((prev) => {
      const projectName = projects.find((p) => p.project_id === pid)?.name || "";
      const current = prev[pid] || { project_id: pid, project_name: projectName, start: null, end: null, equipment_group_ids: [] };
      let end = current.end;
      if (end && date && end < date) end = null;
      return { ...prev, [pid]: { ...current, project_id: pid, project_name: projectName, start: date, end } };
    });
  };

  const setAccessEnd = (pid, date) => {
    const projectName = projects.find((p) => p.project_id === pid)?.name || "";
    setProjectAccess((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || { project_id: pid, project_name: projectName, start: null, end: null, equipment_group_ids: [] }), project_id: pid, project_name: projectName, equipment_group_ids: prev[pid]?.equipment_group_ids || [], end: date },
    }));
  };

  const setProjectEquipmentGroups = (pid, groupIds) => {
    const projectName = projects.find((p) => p.project_id === pid)?.name || "";
    setProjectAccess((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || { project_id: pid, project_name: projectName, start: null, end: null }), project_id: pid, project_name: projectName, equipment_group_ids: groupIds || [] },
    }));
  };

  const handleGroupChange = (e) => {
    const { value } = e.target;
    setFormData((s) => ({ ...s, group: value }));
  };

  const scrollToFirstError = () => {
    // Wait for error state to render, then bring the first error into view.
    setTimeout(() => {
      const el = document.querySelector(".field-error");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  const validate = () => {
    const errs = {};
    if (!formData.name?.trim()) errs.name = "Name is required.";
    if (!formData.email?.trim()) errs.email = "Email is required.";
    if (!formData.phone_number?.trim()) errs.phone = "Phone number is required.";
    else if (!/^\d{10}$/.test(formData.phone_number.trim()))
      errs.phone = "Phone number must be 10 digits.";
    if (!formData.role) errs.role = "Role is required.";
    let hasProjectErrors = false;
    if (selectedProject.length > 0) {
      const missing = [];
      selectedProject.forEach((pid) => {
        const acc = projectAccess[pid] || {};
        if (!acc.start || !acc.end) missing.push(pid);
        else if (acc.end < acc.start) missing.push(pid);
      });
      if (missing.length) {
        setProjectError("Please set valid start/end dates for all selected projects.");
        hasProjectErrors = true;
      } else {
        setProjectError("");
      }
    } else {
      setProjectError("");
    }
    setFieldErrors(errs);
    const hasErrors = Object.keys(errs).length > 0 || hasProjectErrors;
    if (hasErrors) {
      const firstError = Object.values(errs)[0] || "Please set valid start/end dates for all selected projects.";
      setSnackbarMessage(firstError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      scrollToFirstError();
    }
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const user_project = selectedProject.map((pid) => ({
      project_id: pid,
      access_start: formatDateApi(projectAccess[pid]?.start),
      access_end: formatDateApi(projectAccess[pid]?.end),
      equipment_group_ids: Array.isArray(projectAccess[pid].equipment_group_ids) ? projectAccess[pid].equipment_group_ids : [],
    }));
    const payload = {
      name: formData.name,
      email: formData.email,
      aadhar_number: formData.adharnumber || undefined,
      phone_number: formData.phone_number || undefined,
      role_id: formData.role,
      group_ids: formData.group,
      department_ids: formData.department_ids || [],
      user_project,
    };
    try {
      await axiosInstance.put(`${API_URL}/edit_system_user/${user_id}`, payload);
      setSnackbarMessage("System user edited successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Improvement 5 — clear dirty before navigating so beforeunload doesn't fire
      setIsDirty(false);
      setTimeout(() => navigate("/system-users"), 1200);
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || "Something went wrong.";
      const em = String(errorMsg).toLowerCase();
      if (em.includes("email")) setFieldErrors((p) => ({ ...p, email: errorMsg }));
      else if (em.includes("aadhar")) setFieldErrors((p) => ({ ...p, aadhar: errorMsg }));
      else if (em.includes("phone")) setFieldErrors((p) => ({ ...p, phone: errorMsg }));
      else { setSnackbarMessage(errorMsg); setSnackbarSeverity("error"); setSnackbarOpen(true); }
      scrollToFirstError();
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword.trim()) { setPasswordError("Password is required."); return; }
    if (newPassword.length < 6) { setPasswordError("Password must be at least 6 characters."); return; }
    try {
      await axiosInstance.put(`${API_URL}/api/edit_user_password/${user_id}`, { password: newPassword });
      setSnackbarMessage("Password updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setShowPasswordModal(false);
      setNewPassword("");
      setPasswordError("");
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || "Failed to update password.";
      setPasswordError(errorMsg);
    }
  };

  // Improvement 5 — cancel with dirty check
  const handleCancel = () => {
    if (isDirty) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to leave?")) return;
    }
    setIsDirty(false);
    navigate("/system-users");
  };

  const projectById = useMemo(() => {
    const map = new Map();
    projects.forEach((p) => map.set(p.project_id, p));
    return map;
  }, [projects]);

  /** ---------------------- section styles ---------------------- */
  const sectionStyle = {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: "10px",
    padding: "20px 24px",
    marginBottom: "16px",
  };

  const sectionHeaderStyle = {
    fontSize: "13px",
    fontWeight: "600",
    color: "#6b7280",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f0f0f0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  /** ---------------------- UI ---------------------- */
  return (
    <div
      className={`table-container edit-user-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}
      style={{ height: "calc(100vh - 104px)", display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <div className="table-container-content form-main-body" style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden" }}>
        <h1 className="form-titles user-form-title">Edit User</h1>
        <div className="form-body uf-form-scrollbar" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
          {/* Improvement 2 — form gets an id so the external submit button can target it */}
          <form id="edit-user-form" onSubmit={handleSubmit} className="form-box">

            {/* ── Section 1: Personal Information ── */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>Personal Information</div>
              <div className="form-body-container-grid user-form-pg">
                {/* Name */}
                <div className="form-group">
                  <label className="form-label jost-regular">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Name"
                    name="name"
                    value={formData.name}
                    onChange={handleFieldChange}
                    onBlur={() => handleBlur("name")}
                  />
                  {fieldErrors.name && <p className="field-error">{fieldErrors.name}</p>}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label className="form-label jost-regular">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="Enter Email"
                    name="email"
                    value={formData.email}
                    onChange={handleFieldChange}
                    onBlur={() => handleBlur("email")}
                  />
                  {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
                </div>

                {/* Aadhar Number */}
                <div className="form-group">
                  <label className="form-label jost-regular">
                    Aadhar Number
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Aadhar Number"
                    name="adharnumber"
                    value={formData.adharnumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 12);
                      setFormData((p) => ({ ...p, adharnumber: value }));
                      setFieldErrors((p) => ({ ...p, aadhar: "" }));
                    }}
                    onBlur={() => handleBlur("adharnumber")}
                  />
                  {/* Improvement 6 — character counter for adharnumber */}
                  {formData.adharnumber.length > 0 && (
                    <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                      {formData.adharnumber.length}/12 digits
                    </p>
                  )}
                  {fieldErrors.aadhar && <p className="field-error">{fieldErrors.aadhar}</p>}
                </div>

                {/* Phone Number */}
                <div className="form-group">
                  <label className="form-label jost-regular">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleFieldChange}
                    onBlur={() => handleBlur("phone_number")}
                  />
                  {/* Improvement 6 — character counter for phone_number */}
                  {formData.phone_number.length > 0 && (
                    <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                      {formData.phone_number.length}/10 digits
                    </p>
                  )}
                  {fieldErrors.phone && <p className="field-error">{fieldErrors.phone}</p>}
                </div>

                {/* Change Password - Admin only */}
                {isAdmin && (
                  <div className="form-group">
                    <label className="form-label jost-regular">Password</label>
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-secondary border w-100"
                      onClick={() => setShowPasswordModal(true)}
                      style={{ padding: "8px 16px", fontSize: "14px", fontWeight: "500" }}
                    >
                      Change Password
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Section 2: Role & Access ── */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>Role & Access</div>
              <div className="form-body-container-grid user-form-pg">
                {/* Groups */}
                <div className="form-group">
                  <label className="form-label jost-regular">Groups</label>
                  <FormControl fullWidth size="small">
                    {/* Improvement 4 — loading state for Groups Select */}
                    <Select
                      multiple
                      value={formData.group}
                      displayEmpty
                      onChange={handleGroupChange}
                      input={<OutlinedInput label="Select Group" />}
                      disabled={optionsLoading}
                      renderValue={(selected) =>
                        selected.length === 0 ? (
                          <span style={{ color: "#aaa" }}>Select Group</span>
                        ) : (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((value) => {
                              const option = groupOptions.find((opt) => opt.value === value);
                              return (
                                <Chip
                                  key={value}
                                  label={option?.label || value}
                                  onDelete={() => {
                                    const newGroups = formData.group.filter((g) => g !== value);
                                    setFormData((p) => ({ ...p, group: newGroups }));
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                              );
                            })}
                          </Box>
                        )
                      }
                      MenuProps={{ PaperProps: { sx: { maxHeight: 240, overflowY: "auto" } } }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <MenuItem disabled value="" key="placeholder">Select Group</MenuItem>,
                          ...groupOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={formData.group.indexOf(option.value) > -1} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))
                        ]
                      )}
                    </Select>
                  </FormControl>
                </div>

                {/* Designation */}
                <div className="form-group">
                  <label className="form-label jost-regular">Designation</label>
                  <FormControl fullWidth size="small">
                    {/* Improvement 4 — loading state for Designation Select */}
                    <Select
                      multiple
                      value={formData.department_ids}
                      displayEmpty
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      onClose={() => setDeptSearch("")}
                      input={<OutlinedInput label="Select Designation" />}
                      disabled={optionsLoading}
                      renderValue={(selected) =>
                        selected.length === 0 ? (
                          <span style={{ color: "#aaa" }}>Select Designation</span>
                        ) : (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((value) => {
                              const option = departmentOptions.find((opt) => opt.value === value);
                              return (
                                <Chip
                                  key={value}
                                  label={option?.label || value}
                                  onDelete={() => {
                                    const next = formData.department_ids.filter((id) => id !== value);
                                    setFormData((s) => ({ ...s, department_ids: next }));
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                              );
                            })}
                          </Box>
                        )
                      }
                      MenuProps={{
                        PaperProps: { sx: { maxHeight: 320, overflowY: "auto" } },
                        autoFocus: false,
                      }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <ListSubheader sx={{ p: 1, bgcolor: "background.paper" }} key="search">
                            <TextField
                              size="small"
                              fullWidth
                              autoFocus
                              placeholder="Search designation..."
                              value={deptSearch}
                              onChange={(e) => setDeptSearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key !== "Escape") e.stopPropagation();
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <IoSearch />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </ListSubheader>,
                          <MenuItem value="__all__" key="all">
                            <Checkbox checked={allFilteredDeptSelected} />
                            <ListItemText primary={allFilteredDeptSelected ? "Deselect All" : "Select All"} />
                          </MenuItem>,
                          ...(filteredDepartmentOptions.length === 0
                            ? [<MenuItem disabled key="none">No designations found</MenuItem>]
                            : filteredDepartmentOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Checkbox checked={formData.department_ids.indexOf(option.value) > -1} />
                                  <ListItemText primary={option.label} />
                                </MenuItem>
                              ))
                          )
                        ]
                      )}
                    </Select>
                  </FormControl>
                </div>

                {/* Role - Admin only */}
                {isAdmin && (
                  <div className="form-group">
                    <label className="form-label jost-regular">
                      Role <span className="required">*</span>
                    </label>
                    {/* Improvement 4 — loading state for Role select */}
                    <select
                      className="input-field"
                      value={formData.role}
                      onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value }))}
                      disabled={optionsLoading}
                    >
                      {optionsLoading ? (
                        <option>Loading...</option>
                      ) : (
                        <>
                          <option value="" disabled>Select Role</option>
                          {roles.map((role) => (
                            <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                          ))}
                        </>
                      )}
                    </select>
                    {fieldErrors.role && <p className="field-error">{fieldErrors.role}</p>}
                  </div>
                )}

                {/* Projects */}
                <div className="form-group">
                  <label className="form-label jost-regular">Select Project(s)</label>
                  <FormControl fullWidth size="small">
                    {/* Improvement 4 — loading state for Projects Select */}
                    <Select
                      multiple
                      value={selectedProject}
                      onChange={onProjectsChange}
                      input={<OutlinedInput label="Select a Project" />}
                      disabled={optionsLoading}
                      renderValue={(selected) => {
                        if (!selected?.length) return <span>Select a Project</span>;
                        return selected.map((id) => projects.find((p) => p.project_id === id)?.name || id).join(", ");
                      }}
                      displayEmpty
                      MenuProps={{ PaperProps: { sx: { maxHeight: 240, overflowY: "auto" } } }}
                    >
                      {optionsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        [
                          <MenuItem disabled value="" key="placeholder">Select a Project</MenuItem>,
                          ...projects.map((project) => (
                            <MenuItem key={project.project_id} value={project.project_id}>
                              <Checkbox checked={selectedProject.includes(project.project_id)} />
                              {project.name}
                            </MenuItem>
                          ))
                        ]
                      )}
                    </Select>
                  </FormControl>
                  {projectError && <p className="field-error" style={{ marginTop: 6 }}>{projectError}</p>}
                </div>
              </div>
            </div>

            {/* ── Section 3: Project Configurations (conditional) ── */}
            {selectedProject.length > 0 && (
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>Project Configurations</div>
                <div className="uf-edit-project-list">
                  {selectedProject.map((pid) => {
                    const proj = projects.find((p) => p.project_id === pid);
                    const access = projectAccess[pid] || { project_id: pid, project_name: proj?.name || "", start: null, end: null, equipment_group_ids: [] };
                    const eqSearch = equipGroupSearch[pid] || "";
                    const filteredEqGroups = eqSearch.trim()
                      ? equipmentGroupOptions.filter((o) => o.label.toLowerCase().includes(eqSearch.trim().toLowerCase()))
                      : equipmentGroupOptions;
                    const allEqGroupsSelected =
                      filteredEqGroups.length > 0 &&
                      filteredEqGroups.every((o) => (access.equipment_group_ids || []).includes(o.value));
                    // Improvement 3 — accordion collapse
                    const isCollapsed = !!collapsedProjects[pid];

                    return (
                      <div className="uf-project-card" key={pid}>
                        {/* Improvement 3 — clickable header row */}
                        <div className="uf-project-header">
                          <strong className="uf-project-name">{proj?.name || pid}</strong>
                          <button
                            type="button"
                            onClick={() => toggleProject(pid)}
                            className="uf-project-toggle"
                            aria-expanded={!isCollapsed}
                          >
                            <IoChevronDown className={`uf-project-toggle-icon${isCollapsed ? " is-collapsed" : ""}`} />
                            <span>{isCollapsed ? "Show" : "Hide"}</span>
                          </button>
                        </div>
                        {/* Improvement 3 — collapsible content */}
                        <div className="uf-project-body" style={{ display: isCollapsed ? "none" : undefined }}>
                          <section className="uf-project-subsection">
                            <div className="uf-project-subsection-title"><IoCalendarOutline /><span>Timeline</span></div>
                            <div className="row uf-project-date-row">
                            <div className="col-6">
                              <label className="form-label small">Start date</label>
                              <DatePicker
                                selected={access.start}
                                onChange={(d) => setAccessStart(pid, d)}
                                dateFormat="dd-MM-yyyy"
                                className="form-control form-control-sm"
                                placeholderText="dd-mm-yyyy"
                                maxDate={access.end || undefined}
                                isClearable
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label small">End date</label>
                              <DatePicker
                                selected={access.end}
                                onChange={(d) => setAccessEnd(pid, d)}
                                dateFormat="dd-MM-yyyy"
                                className="form-control form-control-sm"
                                placeholderText="dd-mm-yyyy"
                                minDate={access.start || undefined}
                                isClearable
                              />
                            </div>
                            </div>
                            <div className="uf-project-duration">Duration: {getDurationDays(access.start, access.end) ?? "—"} days</div>
                          </section>
                          <section className="uf-project-subsection">
                            <div className="uf-project-subsection-title"><IoPricetagsOutline /><span>Equipment Groups</span></div>
                            <div>
                              <FormControl fullWidth size="small" className="uf-select">
                                <Select
                                  multiple
                                  value={access.equipment_group_ids || []}
                                  displayEmpty
                                  onClose={() => setEquipGroupSearch((prev) => ({ ...prev, [pid]: "" }))}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.includes("__all__")) {
                                      const filteredIds = filteredEqGroups.map((o) => o.value);
                                      const current = access.equipment_group_ids || [];
                                      setProjectEquipmentGroups(
                                        pid,
                                        allEqGroupsSelected
                                          ? current.filter((id) => !filteredIds.includes(id))
                                          : Array.from(new Set([...current, ...filteredIds])),
                                      );
                                      return;
                                    }
                                    setProjectEquipmentGroups(pid, value);
                                  }}
                                  input={<OutlinedInput label="Select equipment groups..." />}
                                  renderValue={(selected) =>
                                    selected.length === 0 ? (
                                      <span className="uf-project-placeholder">Select equipment groups...</span>
                                    ) : (
                                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {selected.map((value) => {
                                          const option = equipmentGroupOptions.find((opt) => opt.value === value);
                                          return (
                                            <Chip
                                              key={value}
                                              label={option?.label || value}
                                              onDelete={() => {
                                                const next = (access.equipment_group_ids || []).filter((id) => id !== value);
                                                setProjectEquipmentGroups(pid, next);
                                              }}
                                              onMouseDown={(e) => e.stopPropagation()}
                                            />
                                          );
                                        })}
                                      </Box>
                                    )
                                  }
                                  MenuProps={{
                                    PaperProps: { sx: { maxHeight: 300, overflowY: "auto" } },
                                    autoFocus: false,
                                  }}
                                >
                                  <ListSubheader sx={{ p: 1, bgcolor: "background.paper" }}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      autoFocus
                                      placeholder="Search equipment groups..."
                                      value={eqSearch}
                                      onChange={(e) => setEquipGroupSearch((prev) => ({ ...prev, [pid]: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key !== "Escape") e.stopPropagation(); }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <IoSearch />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </ListSubheader>
                                  <MenuItem value="__all__">
                                    <Checkbox checked={allEqGroupsSelected} />
                                    <ListItemText primary={allEqGroupsSelected ? "Deselect All" : "Select All"} />
                                  </MenuItem>
                                  {filteredEqGroups.length === 0 ? (
                                    <MenuItem disabled>No equipment groups found</MenuItem>
                                  ) : (
                                    filteredEqGroups.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        <Checkbox checked={(access.equipment_group_ids || []).indexOf(option.value) > -1} />
                                        <ListItemText primary={option.label} />
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>
                            </div>
                          </section>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </form>
        </div>

        {/* Save / Cancel is a non-scrolling sibling of the form body. */}
        <div className="edit-user-footer">
            <button
              type="button"
              className="btn system-user-form-btn system-user-form-btn-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-user-form"
              className="btn system-user-form-btn system-user-form-btn-primary"
            >
              Save
            </button>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="standard">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Password Change Modal */}
      <Dialog
        open={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setNewPassword(""); setPasswordError(""); setShowPassword(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>Change Password</Typography>
          <IconButton onClick={() => { setShowPasswordModal(false); setNewPassword(""); setPasswordError(""); setShowPassword(false); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            label="New Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); }}
            error={!!passwordError}
            helperText={passwordError}
            required
            sx={{ mt: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} onMouseDown={(e) => e.preventDefault()} edge="end" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <button type="button" className="btn btn-outline-secondary px-4 py-2" onClick={() => { setShowPasswordModal(false); setNewPassword(""); setPasswordError(""); setShowPassword(false); }}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary px-4 py-2" onClick={handlePasswordChange}>
            Update Password
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
